import React, { useState, useEffect } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import { masquerAvantEntree } from '../utils/periodeLocataire';
import '../styles/ExpensesChart.css';

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

interface CategoryExpense {
  categoryId: string;
  categoryName: string;
  amount: number;
}

interface MonthlyExpense {
  month: number;
  year: number;
  categoryExpenses: CategoryExpense[];
  totalAmount: number;
}

interface Category {
  id: string;
  name: string;
}

interface ExpenseAnalytics {
  monthlyExpenses: MonthlyExpense[];
  categories: Category[];
  year: number;
}

interface ExpensesChartProps {
  data: ExpenseAnalytics | null;
  loading: boolean;
  error: string | null;
  onYearChange: (year: number) => void;
  /** Entrée dans les lieux, sur l'espace locataire : les mois d'avant sont
   *  laissés vides. Cf. `utils/periodeLocataire`. Absente côté propriétaire. */
  depuis?: Date | null;
}

// Month names in French
const MONTH_NAMES = [
  'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
  'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'
];

// Generate random colors for categories that will stay consistent
const generateCategoryColors = (categories: Category[]) => {
  const colorMap: Record<string, string> = {};
  
  // Predefined colors for better UI (can be expanded)
  const baseColors = [
    'rgba(255, 99, 132, 0.8)',   // Red
    'rgba(54, 162, 235, 0.8)',   // Blue
    'rgba(255, 206, 86, 0.8)',   // Yellow
    'rgba(75, 192, 192, 0.8)',   // Teal
    'rgba(153, 102, 255, 0.8)',  // Purple
    'rgba(255, 159, 64, 0.8)',   // Orange
    'rgba(199, 199, 199, 0.8)',  // Gray
    'rgba(83, 102, 255, 0.8)',   // Indigo
    'rgba(255, 99, 255, 0.8)',   // Pink
    'rgba(99, 255, 132, 0.8)',   // Light green
  ];
  
  categories.forEach((category, index) => {
    colorMap[category.id] = baseColors[index % baseColors.length];
  });
  
  // Add a default color for uncategorized
  colorMap['uncategorized'] = 'rgba(200, 200, 200, 0.8)';
  
  return colorMap;
};

const ExpensesChart: React.FC<ExpensesChartProps> = ({ 
  data, 
  loading, 
  error, 
  onYearChange,
  depuis = null,
}) => {
  const [categoryColors, setCategoryColors] = useState<Record<string, string>>({});
  const currentYear = new Date().getFullYear();
  const [selectedYear, setSelectedYear] = useState<number>(currentYear);
  
  useEffect(() => {
    if (data?.categories) {
      setCategoryColors(generateCategoryColors(data.categories));
    }
  }, [data?.categories]);
  
  const handleYearChange = (change: number) => {
    const newYear = selectedYear + change;
    setSelectedYear(newYear);
    onYearChange(newYear);
  };
  
  if (loading) {
    return <div className="expenses-chart-loading">Chargement des données...</div>;
  }
  
  if (error) {
    return <div className="expenses-chart-error">Erreur: {error}</div>;
  }
  
  if (!data || !data.monthlyExpenses) {
    return <div className="expenses-chart-empty">Aucune donnée disponible</div>;
  }
  
  // Quels mois de l'année AFFICHÉE concernent le locataire. `data.year` et non
  // l'année courante : ce graphique navigue d'une année à l'autre, et une année
  // entièrement antérieure à son entrée doit rester vide.
  const visible = masquerAvantEntree(Array(12).fill(1), data.year, depuis);
  const masque = (i: number) => visible[i] == null;

  // Prepare data for the stacked bar chart
  const chartData = {
    labels: MONTH_NAMES,
    datasets: data.categories.map(category => {
      return {
        label: category.name,
        data: data.monthlyExpenses.map((month, i) => {
          if (masque(i)) return null;
          const categoryExpense = month.categoryExpenses.find(
            ce => ce.categoryId === category.id
          );
          return categoryExpense ? categoryExpense.amount : 0;
        }),
        backgroundColor: categoryColors[category.id] || 'rgba(0, 0, 0, 0.1)',
      };
    }),
  };
  
  // Add uncategorized expenses if they exist
  const hasUncategorized = data.monthlyExpenses.some(
    month => month.categoryExpenses.some(ce => ce.categoryId === 'uncategorized')
  );
  
  if (hasUncategorized) {
    chartData.datasets.push({
      label: 'Non catégorisé',
      data: data.monthlyExpenses.map((month, i) => {
        if (masque(i)) return null;
        const uncategorized = month.categoryExpenses.find(
          ce => ce.categoryId === 'uncategorized'
        );
        return uncategorized ? uncategorized.amount : 0;
      }),
      backgroundColor: 'rgba(200, 200, 200, 0.8)',
    });
  }
  
  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      x: {
        stacked: true,
      },
      y: {
        stacked: true,
        ticks: {
          callback: (value: any) => `${value} €`,
        },
      },
    },
    plugins: {
      tooltip: {
        callbacks: {
          label: function(context: any) {
            const value = context.raw || 0;
            return `${context.dataset.label}: ${value.toFixed(2)} €`;
          },
          footer: function(tooltipItems: any) {
            const monthIndex = tooltipItems[0].dataIndex;
            // Sans ce garde, un mois masqué affichait quand même son total :
            // la barre disparaissait, le chiffre restait.
            if (masque(monthIndex)) return '';
            const totalAmount = data.monthlyExpenses[monthIndex].totalAmount;
            return `Total: ${totalAmount.toFixed(2)} €`;
          }
        }
      },
      legend: {
        position: 'bottom' as const,
      },
      title: {
        display: true,
        text: `Dépenses mensuelles ${data.year}`,
        font: {
          size: 16,
        },
      },
    },
  };
  
  return (
    <div className="expenses-chart-container">
      <div className="year-navigation">
        <button 
          className="year-nav-button"
          onClick={() => handleYearChange(-1)}
        >
          &lt; {selectedYear - 1}
        </button>
        <span className="current-year">{selectedYear}</span>
        <button 
          className="year-nav-button"
          onClick={() => handleYearChange(1)}
          disabled={selectedYear >= currentYear}
        >
          {selectedYear + 1} &gt;
        </button>
      </div>
      
      <div className="chart-wrapper">
        <Bar data={chartData} options={chartOptions} />
      </div>
      
      <div className="category-legend">
        <h4>Catégories de dépenses</h4>
        <div className="legend-items">
          {data.categories.map(category => (
            <div key={category.id} className="legend-item">
              <span 
                className="color-box" 
                style={{ backgroundColor: categoryColors[category.id] }}
              ></span>
              <span className="category-name">{category.name}</span>
            </div>
          ))}
          {hasUncategorized && (
            <div className="legend-item">
              <span 
                className="color-box" 
                style={{ backgroundColor: 'rgba(200, 200, 200, 0.8)' }}
              ></span>
              <span className="category-name">Non catégorisé</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ExpensesChart;
